import { useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateProposal, useUpdateProposal } from "creso-core/hooks";
import { Proposal } from "creso-core/types";
import { z } from "zod";
import {
  ProposalFormSchema,
  useProposalFormContext,
} from "../providers/proposal-provider";

// Step-up only applies to SIPs, so drop it when there's no SIP amount
const getStepUpPayload = (data: z.infer<typeof ProposalFormSchema>) => {
  const hasStepUp =
    !!data.step_up_frequency && Number(data.total_sip_amount) > 0;
  return {
    step_up_frequency: hasStepUp ? data.step_up_frequency : null,
    step_up_amount: hasStepUp ? data.step_up_amount : null,
    step_up_percentage: hasStepUp ? data.step_up_percentage : null,
  };
};

/**
 * Persists the top-level proposal fields (name, amounts, return/duration, status)
 * from the shared form. Any step that reads `total_lumpsum_amount`/`total_sip_amount`
 * downstream (e.g. the benchmark backtest, which runs server-side off the saved
 * proposal record) must call this before relying on those values being current -
 * sidebar step navigation does not save automatically.
 */
export function useSaveProposal() {
  const form = useProposalFormContext();
  const createProposalMutation = useCreateProposal();
  const updateProposalMutation = useUpdateProposal();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useCallback(
    (activate = false) => {
      return new Promise<Proposal>((res, rej) => {
        form.handleSubmit(
          async (data: z.infer<typeof ProposalFormSchema>) => {
            try {
              if (data.id) {
                const result = await updateProposalMutation.mutateAsync({
                  id: data.id,
                  payload: {
                    name: data.name,
                    total_sip_amount: data.total_sip_amount,
                    total_lumpsum_amount: data.total_lumpsum_amount,
                    projected_value: data.projected_value ?? null,
                    include_remarks: data.include_remarks ?? true,
                    expected_return: data.expected_return,
                    duration_months: data.duration_months,
                    ...getStepUpPayload(data),
                    status: activate ? "APPROVED" : "DRAFT",
                  },
                });
                res(result);
              } else {
                const proposal = await createProposalMutation.mutateAsync({
                  name: data.name,
                  client: data.client,
                  total_sip_amount: data.total_sip_amount,
                  total_lumpsum_amount: data.total_lumpsum_amount,
                  projected_value: data.projected_value ?? null,
                  include_remarks: data.include_remarks ?? true,
                  expected_return: data.expected_return,
                  duration_months: data.duration_months,
                  ...getStepUpPayload(data),
                  status: activate ? "APPROVED" : "DRAFT",
                  notes: "",
                  goal: data.goal?.id,
                });
                if (data.goal?.id) {
                  queryClient.invalidateQueries({
                    queryKey: ["goals"],
                  });
                }
                navigate({
                  to: ".",
                  search: (prev) => ({
                    ...prev,
                    proposal_id: proposal.id,
                    state:
                      Number(data.total_lumpsum_amount) > 0 ? "lumpsum" : "sip",
                  }),
                  replace: true,
                });
                res(proposal);
              }
            } catch (e) {
              rej(e);
            }
          },
          (errors) => rej(errors)
        )();
      });
    },
    [
      form,
      updateProposalMutation,
      createProposalMutation,
      navigate,
      queryClient,
    ]
  );
}
